import isUndefined from 'lodash/isUndefined';
import isArray from 'lodash/isArray';
import isString from 'lodash/isString';
import isNull from 'lodash/isNull';
import isToday from 'date-fns/is_today';
import { rewriteUrl } from '~shared/utils/uriAliases';
import i18n from '../../utils/i18n';
import { getTournamentLogoUrl } from '~shared/utils/sportsbook';

import { create, nextRepaint, removeChildren } from '../../utils/dom';
import {
    CONTENT_SECTION_CLASS_BLOCK_NAME,
    CONTENT_SECTION_HEADER_CLASS_NAME,
    CONTENT_SECTION_TITLE_CLASS_NAME,
    CONTENT_SECTION_MODIFIER_TITLE_TEXT_WRAPPED,
    CONTENT_SECTION_TITLE_CONTAINER_CLASS_NAME,
    CONTENT_SECTION_TITLE_ITEM_CLASS_NAME,
    CONTENT_SECTION_COLLAPSED_CLASS_NAME,
    CONTENT_SECTION_HIDDEN_CONTAINER_CLASS_NAME,
    CONTENT_SECTION_COLLAPSED_EVENT,
    CONTENT_SECTION_EXPANDED_EVENT,
    CONTENT_SECTION_MODIFIER_SPORT_ACCOUNT,
    CONTENT_SECTION_MODIFIER_SECONDARY,
    CONTENT_SECTION_MODIFIER_TERTIARY,
    CONTENT_SECTION_FOOTER_CLASS_NAME,
    CONTENT_SECTION_LINK_CLASS_NAME,
    CONTENT_SECTION_MODIFIER_QUATERNARY,
    CONTENT_SECTION_MODIFIER_DEFAULT,
    GTM_CONTENT_SECTION_ATTR_NAME,
    CONTENT_SECTION_SEE_ALL_CLASS_NAME,
    CONTENT_SECTION_MODIFIER_SEE_ALL,
    CONTENT_SECTION_EXPAND,
    CONTENT_SECTION_COLLAPSE,
    CONTENT_SECTION_TOURNAMENT_LOGO_CLASS_NAME,
    CONTENT_SECTION_TOURNAMENT_LOGO_ICON_CLASS_NAME,
} from './constants';
import { createEventEmitter } from '../../utils/event';
import { stopJqueryMobileEventListeners } from '../../utils/dom';
import { renderDateEl } from '../../ui/Time/TimePortalUtil';
import Icon from '../../components/icon';
import {
    ICON_ORANGE_DROPDOWN,
    WIDTH_12,
    WIDTH_15,
    ROTATE_180,
} from '../../components/icon/constants';
import {
    seoUrlInplaySport,
    seoUrlTournament,
 } from '~spa/Utils/SeoUrls';
import {
    GTM_ATTRIBUTE_INPLAY,
} from '~scenes/inplay/components/constants';

import './styles.scss';

/**
 * Creates a content section. Optionally collapsible.
 * @param  {String|Array} headerText The text in the header
 * @param  {Boolean} [isCollapsible=true] Whether the section is collapsible or not
 * @param  {Array} [classList=[]] A list of classes to add to the section
 * @param  {Array} [titleModifiers=[]] A list of css modifiers to apply to the header
 * @return {Object} The ContentSection component
 */
export default function createContentSection({
    headerText = null,
    wrapHeaderText = false,
    viewAllLinkOptions = null,
    seeMore = null,
    isCollapsible = true,
    classList = [],
    titleModifiers = [],
    icon_type = ICON_ORANGE_DROPDOWN,
    footerText = null,
    footerLink = null,
    footerLinkModifiers = [],
    gtmAttribute,
    isTournament = false,
    isFavouriteSport = false,
    dataTestId,
    tournamentLogos = null,
    sportSlug = null,
    categorySlug = null,
    tournamentSlug = null,
} = {}) {
    /**
    * Event emitter for the component
    * @type {EventEmitter}
    */
    const ev = createEventEmitter();

    const el = create('section', CONTENT_SECTION_CLASS_BLOCK_NAME);

    classList.forEach(addClass);

    let sportLink = null;
    let header = null;
    let titleEl = null;
    let dropdownIcon = null;
    let footer = null;
    let footerLinkEl = null;
    let seeAll = null;

    if (headerText) {

        header = create('header', CONTENT_SECTION_HEADER_CLASS_NAME);

        stopJqueryMobileEventListeners(header);

        const headerTextEl = createHeaderTextEl(headerText);
        titleEl = create('h2', CONTENT_SECTION_TITLE_CLASS_NAME, headerTextEl);
        if (wrapHeaderText) {
            addTitleModifier(CONTENT_SECTION_MODIFIER_TITLE_TEXT_WRAPPED);
        }

        if (isFavouriteSport) {
            const favouriteStar = create('div', `${CONTENT_SECTION_TITLE_CLASS_NAME}__favourite-star`);
            titleEl.appendChild(favouriteStar);
        }

        if (viewAllLinkOptions) {
            const {
                sportSlug,
                numberOfEvents = 1,
                translatedSportSlug,
                sportId,
                setViewAllLink,
            } = viewAllLinkOptions;
            sportLink = create('a');
            sportLink.setAttribute('href', rewriteUrl(seoUrlInplaySport(translatedSportSlug, sportId)));
            sportLink.appendChild(titleEl);

            const getEventText = (numberOfEvents = 1) => {
                return i18n.t('inplay.event', {count: numberOfEvents});
            };

            const numberOfEventsText = create('span', 'bb-inplay-sport__view-all-link-text', getEventText(numberOfEvents));
            const numberOfEventsEl = create('span', 'bb-inplay-sport__view-all-link', numberOfEventsText);

            const gtm = `${GTM_ATTRIBUTE_INPLAY};all-sports;${sportSlug}-see-more`;
            [titleEl, sportLink, numberOfEventsText, numberOfEventsEl].forEach(el => {
                el.setAttribute('data-spectate-product', gtm);
            });

            const setNumberEvents = (numberOfEvents) => {
                nextRepaint(() => {
                    numberOfEventsText.innerHTML = getEventText(numberOfEvents);
                });
            };

            setViewAllLink({
                el: numberOfEventsEl,
                setNumberEvents,
            });

            titleEl.appendChild(numberOfEventsEl);
        }

        if (seeMore) {
            const {
                sportSlug,
                tournamentSlug,
                categorySlug,
                tournamentId,
            } = seeMore;

            sportLink = create('a');
            sportLink.setAttribute('href', rewriteUrl(seoUrlTournament(`${sportSlug}/${categorySlug}/${tournamentSlug}`, tournamentId)));
            sportLink.appendChild(titleEl);
        }

        titleModifiers.forEach(addTitleModifier);

        if (isCollapsible) {
            header.addEventListener('click', toggleCollapse);

            const shouldUseExpandArrow = titleModifiers.some(modifier => (
                [
                    CONTENT_SECTION_MODIFIER_QUATERNARY,
                    CONTENT_SECTION_MODIFIER_TERTIARY,
                    CONTENT_SECTION_MODIFIER_SECONDARY,
                    CONTENT_SECTION_MODIFIER_DEFAULT,
                ].includes(modifier)
            ));

            const iconWidth = titleModifiers.includes(CONTENT_SECTION_MODIFIER_SPORT_ACCOUNT) ? WIDTH_15 : WIDTH_12;
            const iconType = shouldUseExpandArrow
                ? ICON_ORANGE_DROPDOWN
                : icon_type;

            dropdownIcon = new Icon(iconType, iconWidth);
            dropdownIcon.el.classList.add('bb-content-section__dropdown-icon');
            dropdownIcon.addModifier(ROTATE_180);
            if (gtmAttribute) {
                titleEl.setAttribute('data-spectate-product', `${gtmAttribute};${CONTENT_SECTION_COLLAPSE}`);
            }
            if (dataTestId) {
                titleEl.setAttribute('data-test-id', dataTestId);
            }
            titleEl.appendChild(dropdownIcon.el);
        }

        if (tournamentLogos && tournamentLogos.showTournamentLogo) {
            titleEl.classList.add(
                CONTENT_SECTION_TOURNAMENT_LOGO_CLASS_NAME,
            );
            const tournamentLogo = create('img', CONTENT_SECTION_TOURNAMENT_LOGO_ICON_CLASS_NAME);
            tournamentLogo.setAttribute('src', getTournamentLogoUrl(sportSlug, categorySlug, tournamentSlug));
            titleEl.insertBefore(tournamentLogo, titleEl.firstChild);
        }

        // sport link will only be defined if viewAllLinkOptions exists.
        // if the sport link exists titleEl will be a child of it.
        const title = sportLink || titleEl;

        if (footerText && isTournament) {
            const seeAllTextEl = createFooterTextEl(footerText, footerLink);
            addTitleModifier(CONTENT_SECTION_MODIFIER_SEE_ALL);

            seeAll = seeAllTextEl;
            footerLinkModifiers.forEach(addFooterLinkModifier);

            seeAll.prepend(title);
            header.appendChild(seeAll);
        } else {
            header.appendChild(title);
        }
    }

    footer = null;
    footerLinkEl = null;
    if (footerText && !isTournament) {
        footer = create('footer', CONTENT_SECTION_FOOTER_CLASS_NAME);

        const footerTextEl = createFooterTextEl(footerText, footerLink);
        if (footerTextEl instanceof Node) {
            footer.appendChild(footerTextEl);
            footerLinkModifiers.forEach(addFooterLinkModifier);
        } else {
            footer.innerHTML = footerTextEl;
        }
    }

    const contentContainer = create('div', 'bb-content-section__content');

    if (header) {
        el.appendChild(header);
    }
    el.appendChild(contentContainer);
    if (footer) {
        el.appendChild(footer);
    }

    /**
     * Add a class to the classList of the main element
     * @param {String} className The class to add
     * @return {void}
     */
    function addClass(className) {
        el.classList.add(className);
    }

    /**
     * Adds a class modifier to the header (title) element.
     * @param {String} modifier The modifier
     * @return {void}
     */
    function addTitleModifier(modifier) {
        addTitleClassName(`${CONTENT_SECTION_TITLE_CLASS_NAME}--${modifier}`);
    }

    /**
     * Add a class to the title element
     * @param {String} className The class name to add
     * @return {void}
     */
    function addTitleClassName(className) {
        titleEl?.classList.add(className); // eslint-disable-line no-unused-expressions
    }

    /**
     * Adds a class modifier to the header (title) element.
     * @param {String} modifier The modifier
     * @return {void}
     */
    function addFooterLinkModifier(modifier) {
        addFooterLinkClassName(`${CONTENT_SECTION_LINK_CLASS_NAME}--${modifier}`);
    }

    /**
     * Add a class to the title element
     * @param {String} className The class name to add
     * @return {void}
     */
    function addFooterLinkClassName(className) {
        if (footerLinkEl) {
            footerLinkEl.classList.add(className);
        }
    }

    /**
     * Toggle collapsed state
     * @param  {Event} [event] The optional dom event
     * @return {void}
     */
    function toggleCollapse(event) {
        if (!isUndefined(event)) {
            event.stopPropagation();
        }

        if (isCollapsed()) {
            showAsExpanded();
        } else {
            showAsCollapsed();
        }
    }

    /**
     * Displays the section as collapsed
     * @return {void}
     */
    function showAsCollapsed() {
        if (isCollapsible === false) {
            return;
        }

        nextRepaint(()=> {
            el.classList.add(CONTENT_SECTION_COLLAPSED_CLASS_NAME);
            contentContainer.classList.add(CONTENT_SECTION_HIDDEN_CONTAINER_CLASS_NAME);
            dropdownIcon.removeModifier(ROTATE_180);
            if (gtmAttribute) {
                titleEl.setAttribute('data-spectate-product', `${gtmAttribute};${CONTENT_SECTION_EXPAND}`);
            }
        });

        ev.emit(CONTENT_SECTION_COLLAPSED_EVENT);
    }

    /**
     * Displays the section as expanded
     * @return {void}
     */
    function showAsExpanded() {
        if (isCollapsible === false) {
            return;
        }

        nextRepaint(()=> {
            el.classList.remove(CONTENT_SECTION_COLLAPSED_CLASS_NAME);
            contentContainer.classList.remove(CONTENT_SECTION_HIDDEN_CONTAINER_CLASS_NAME);
            dropdownIcon.addModifier(ROTATE_180);
            if (gtmAttribute) {
                titleEl.setAttribute('data-spectate-product', `${gtmAttribute};${CONTENT_SECTION_COLLAPSE}`);
            }
        });

        ev.emit(CONTENT_SECTION_EXPANDED_EVENT);
    }

    /**
     * Checks if the section is collapsed
     * @return {Boolean} returns true if section has collapsed class
     */
    function isCollapsed() {
        return el.classList.contains(`${CONTENT_SECTION_COLLAPSED_CLASS_NAME}`);
    }

    /**
     * Clears the content and then appends the DOM element
     * @param {Object} contentEl The DOM element to append as content
     * @return {void}
     */
    function setContent(contentEl) {
        removeChildren(contentContainer);
        appendContent(contentEl);
    }

    /**
     * Appends the DOM element to the ContentContainer
     * @param {Object} contentEl The DOM element to append as content
     * @return {void}
     */
    function appendContent(contentEl) {
        contentContainer.appendChild(contentEl);
    }

    /**
     * Appends the DOM element to the Header
     * @param {Object} contentEl The DOM element to append as content
     * @return {void}
     */
    function appendHeaderContent(contentEl) {
        header.appendChild(contentEl);
    }

    /**
     * Creates header text element
     * @param {String|Object} headerText The content
     * @return {Object|String} The header text elements or string
     */
    function createHeaderTextEl(headerText) {
        let headerTextEl;

        if (isArray(headerText)) {
            headerTextEl = create('div', CONTENT_SECTION_TITLE_CONTAINER_CLASS_NAME);
            headerText.forEach(columnLabel => {
                if (!isNull(columnLabel) && typeof columnLabel === 'object') {
                    let column;
                    if (!isToday(columnLabel.dateTime)) {
                        column = create('span', CONTENT_SECTION_TITLE_ITEM_CLASS_NAME);
                        renderDateEl({...columnLabel}, column);
                    } else {
                        column = create('span', CONTENT_SECTION_TITLE_ITEM_CLASS_NAME, i18n.t('sports.today'));
                    }
                    headerTextEl.appendChild(column);
                } else {
                    const column = create('span', CONTENT_SECTION_TITLE_ITEM_CLASS_NAME, columnLabel);
                    headerTextEl.appendChild(column);
                }
            });
        } else {
            headerTextEl = create('span', 'bb-content-section__title-text', headerText);
        }

        return headerTextEl;
    }

    /**
     * Updates the header content
     * @param {String|Object} headerText The content
     * @return {void}
     */
    function setHeaderContent(headerText) {
        removeChildren(titleEl);
        if (isArray(headerText)) {
            titleEl.appendChild(createHeaderTextEl(headerText));
        } else if (isString(headerText)) {
            titleEl.innerHTML = headerText;
        } else {
            removeChildren(header);
        }
    }

    /**
     * Creates footer text element
     * @param {String} footerText The content
     * @param {String} footerLink The link
     * @return {Object|String} The footer text elements or string
     */
    function createFooterTextEl(footerText, footerLink) {
        let footerTextEl;
        const linkClassName = (!isTournament) ? CONTENT_SECTION_LINK_CLASS_NAME : '';

        if (footerLink) {
            footerTextEl = create('a', linkClassName);
            if (isTournament) {
                const linkWrapper = create('span', CONTENT_SECTION_SEE_ALL_CLASS_NAME, i18n.t('betcard.see_all'));
                footerTextEl.appendChild(linkWrapper);
            } else {
                footerTextEl.innerHTML = footerText;
            }
            footerTextEl.setAttribute(GTM_CONTENT_SECTION_ATTR_NAME, gtmAttribute);
            footerTextEl.href = rewriteUrl(footerLink);

            footerLinkEl = footerTextEl;
        } else {
            footerTextEl = footerText;
        }

        return footerTextEl;
    }

    /**
     * Updates the footer link
     * @param {String} footerLink The link
     * @return {void}
     */
    function setFooterLink(footerLink) {
        const linkEl = footer?.querySelector('a');
        if (linkEl) {
            linkEl.setAttribute('href', rewriteUrl(footerLink));
        }
    }

    return {
        el,
        ev,
        appendHeaderContent,
        addTitleModifier,
        addTitleClassName,
        addFooterLinkModifier,
        addFooterLinkClassName,
        toggleCollapse,
        showAsCollapsed,
        showAsExpanded,
        isCollapsed,
        setContent,
        appendContent,
        setHeaderContent,
        setFooterLink,
    };
}
